/**
 * SkuDetail — generic SKU detail screen at /sku/:code
 *
 * Reads :code via useParams, resolves the SKU via useSku (piggybacks on the
 * ['skus'] cache from the catalog; falls back to a live fetch on deep-link).
 *
 * Layout:
 *   CosmosBackdrop (decorative)
 *   Header row: back chevron → navigate(-1) or /catalog fallback
 *   Hero: LivingSigil (breathing, size 220) + eyebrow + title + description
 *   Meta row: category chip + price chip
 *   CTA area (status-aware):
 *     LIVE + flow registered  → "Begin the rite" → navigate(flowRoute)
 *     LIVE + stub             → "Begin the rite" disabled + caption
 *     SOON                    → inline notify via useSkuNotify
 *     HIDDEN / not found      → not-found state
 *
 * Accessibility:
 *   - Back button aria-label="Back"
 *   - main role="main" aria-labelledby="sku-title"
 *   - Live region for SOON notify confirmation (aria-live="polite")
 *   - prefers-reduced-motion respected in CSS module
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../components/brand/CosmosBackdrop';
import { LivingSigil } from '../components/brand/LivingSigil';
import { useSku } from '../hooks/useSku';
import { useSkuNotify } from '../hooks/useSkuNotify';
import { useSkuLabel } from '../lib/sku-i18n';
import { getSkuFlow } from '../lib/sku-flow-registry';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { InvoiceSheetShell } from '../components/payment/InvoiceSheetShell';
import { ExamplesCarousel } from '../components/skuDetail/ExamplesCarousel';
import { exampleUrl, getExamplesFolderForSku } from '../lib/sku-examples';
import { skuGlyph } from '../lib/sku-glyph';
import { AppHeader } from '../components/chrome/AppHeader';
import { PrimaryButton, SecondaryButton } from '../components/chrome/Button';
import styles from './SkuDetail.module.css';

// --------------------------------------------------------------------------
// Sub-components
// --------------------------------------------------------------------------

function LoadingSkeleton(): JSX.Element {
  const { t: tCommon } = useTranslation('common');
  return (
    <div className={styles.skeletonBlock} aria-busy="true" aria-label={tCommon('states.loadingRite')}>
      <div
        className={styles.skeletonSigil}
        role="presentation"
        aria-hidden="true"
        data-testid="sku-detail-skeleton-sigil"
      />
      <div className={styles.skeletonLines}>
        <div
          className={`${styles.skeletonLine} ${styles['skeletonLine--title']}`}
          role="presentation"
          aria-hidden="true"
          data-testid="sku-detail-skeleton-line"
        />
        <div
          className={`${styles.skeletonLine} ${styles['skeletonLine--desc']}`}
          role="presentation"
          aria-hidden="true"
          data-testid="sku-detail-skeleton-line"
        />
        <div
          className={`${styles.skeletonLine} ${styles['skeletonLine--desc2']}`}
          role="presentation"
          aria-hidden="true"
          data-testid="sku-detail-skeleton-line"
        />
      </div>
    </div>
  );
}

function NotFoundState(): JSX.Element {
  const { t } = useTranslation('flows');
  return (
    <div className={styles.notFoundBlock} data-testid="sku-detail-not-found">
      <p className={styles.notFoundTitle}>{t('skuDetail.notFound.title')}</p>
      <p className={styles.notFoundDesc}>{t('skuDetail.notFound.desc')}</p>
      <Link to="/catalog" className={styles.catalogLink}>
        {t('skuDetail.notFound.back')}
      </Link>
    </div>
  );
}

// --------------------------------------------------------------------------
// CTA variants
// --------------------------------------------------------------------------

type NotifyState = 'idle' | 'loading' | 'confirmed' | 'error';

interface CtaAreaProps {
  code: string;
  status: 'LIVE' | 'SOON' | 'HIDDEN';
}

function CtaArea({ code, status }: CtaAreaProps): JSX.Element {
  const navigate = useNavigate();
  const { notifySku } = useSkuNotify();
  const { t } = useTranslation(['flows', 'common']);
  const [notifyState, setNotifyState] = useState<NotifyState>('idle');

  if (status === 'LIVE') {
    const flow = getSkuFlow(code);
    const isLive = flow.kind === 'live';
    const isNotImplemented = flow.kind === 'not_implemented';

    return (
      <div className={styles.ctaArea}>
        <PrimaryButton
          disabled={!isLive}
          aria-disabled={!isLive}
          onClick={() => {
            if (isLive && flow.kind === 'live') {
              void navigate(flow.flowRoute);
            }
          }}
          data-testid="sku-detail-begin-btn"
        >
          {t('skuDetail.cta.begin')}
        </PrimaryButton>
        {isNotImplemented && (
          <p
            className={styles.ctaCaption}
            data-testid="sku-detail-not-implemented-caption"
          >
            {t('skuDetail.cta.notImplemented')}
          </p>
        )}
        {!isLive && !isNotImplemented && (
          <p className={styles.ctaCaption} data-testid="sku-detail-stub-caption">
            {t('skuDetail.cta.availableSoon')}
          </p>
        )}
      </div>
    );
  }

  if (status === 'SOON') {
    async function handleNotify() {
      if (notifyState === 'loading' || notifyState === 'confirmed') return;
      setNotifyState('loading');
      try {
        await notifySku(code);
        setNotifyState('confirmed');
      } catch {
        setNotifyState('error');
      }
    }

    return (
      <div className={styles.ctaArea}>
        {/* aria-live region wraps the whole CTA area to announce state changes */}
        <div aria-live="polite" aria-atomic="true">
          {notifyState === 'confirmed' ? (
            <div className={styles.confirmedRow} data-testid="sku-detail-confirmed">
              <span className={styles.checkMark} aria-hidden="true">✓</span>
              <span>{t('skuDetail.notify.confirmed')}</span>
            </div>
          ) : (
            <>
              <SecondaryButton
                disabled={notifyState === 'loading'}
                aria-disabled={notifyState === 'loading'}
                onClick={handleNotify}
                data-testid="sku-detail-notify-btn"
              >
                {notifyState === 'loading' ? t('common:states.saving') : t('skuDetail.notify.cta')}
              </SecondaryButton>
              {notifyState === 'error' && (
                <p
                  className={styles.notifyError}
                  role="alert"
                  data-testid="sku-detail-notify-error"
                >
                  {t('skuDetail.notify.error')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // HIDDEN or unexpected — caller should not reach this; renders nothing.
  return <></>;
}

// --------------------------------------------------------------------------
// Price chip helper
// --------------------------------------------------------------------------

function PriceChip({
  priceStars,
  releaseTarget,
  status,
}: {
  priceStars: number | null;
  releaseTarget: string | null;
  status: 'LIVE' | 'SOON' | 'HIDDEN';
}): JSX.Element {
  const { t } = useTranslation('flows');
  if (status === 'SOON') {
    const label = releaseTarget
      ? t('skuDetail.price.soon', { target: releaseTarget })
      : t('skuDetail.price.soonTba');
    return (
      <span className={`${styles.chip} ${styles.chipSoon}`} data-testid="sku-detail-price-chip">
        {label}
      </span>
    );
  }
  if (priceStars == null) {
    return (
      <span className={`${styles.chip} ${styles.chipGold}`} data-testid="sku-detail-price-chip">
        {t('skuDetail.price.free')}
      </span>
    );
  }
  return (
    <span className={`${styles.chip} ${styles.chipGold}`} data-testid="sku-detail-price-chip">
      {priceStars} ⭐
    </span>
  );
}

// --------------------------------------------------------------------------
// Main screen
// --------------------------------------------------------------------------

export function SkuDetail(): JSX.Element {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['flows', 'common']);
  const { t: tCommon } = useTranslation('common');
  const skuLabel = useSkuLabel();
  const { sku, isLoading, isError, refetch } = useSku(code);

  // Avatar SKUs (live flow + metadata available) render the AM1 invoice sheet
  // directly over CosmosBackdrop — matches design source flow-avatar-mini.jsx
  // function AM1Invoice() (lines 107–213). On Pay → start the avatar flow.
  const avatarMeta = sku ? (SKU_CATALOG[sku.code] ?? null) : null;
  const flow = sku ? getSkuFlow(sku.code) : null;
  if (sku && sku.status === 'LIVE' && avatarMeta && flow && flow.kind === 'live') {
    const name = skuLabel(sku.nameKey);
    const description = skuLabel(sku.descKey);
    const priceStars = sku.priceStars ?? 0;
    const folder = getExamplesFolderForSku(sku.code);
    const thumbUrl = folder ? exampleUrl(folder, 1) : undefined;
    const rawBullets = tCommon(`sku.${sku.code}.bullets`, { returnObjects: true, defaultValue: [] });
    const bullets = Array.isArray(rawBullets) ? (rawBullets as string[]) : [];
    return (
      <div className={styles.root} data-testid={`sku-detail-${sku.code}`}>
        <CosmosBackdrop />
        <div className={styles.invoiceHeaderWrap}>
          <AppHeader
            title={name}
            onBack={() => {
              if (window.history.length > 1) navigate(-1);
              else void navigate('/catalog');
            }}
            backTestId="sku-detail-back-btn"
            headingId="sku-detail-heading"
          />
        </div>
        <ExamplesCarousel skuCode={sku.code} />
        <InvoiceSheetShell
          skuCode={sku.code}
          title={name}
          subtitle={description || undefined}
          priceStars={priceStars}
          bullets={bullets}
          thumbUrl={thumbUrl}
          glyph={skuGlyph(sku.code)}
          ctaLabel={t('skuDetail.cta.proceed', { defaultValue: 'Proceed' })}
          onPrimary={() => navigate(flow.flowRoute)}
          onCancel={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate('/catalog');
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <CosmosBackdrop />

      <main
        role="main"
        aria-labelledby="sku-detail-heading"
        className={styles.content}
      >
        {/* Header row — always visible */}
        <AppHeader
          title={skuLabel(sku?.nameKey ?? '') || t('flows:skuDetail.headerTitle')}
          onBack={() => { if (window.history.length > 1) navigate(-1); else void navigate('/catalog'); }}
          backTestId="sku-detail-back-btn"
          headingId="sku-detail-heading"
        />

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {isLoading && <LoadingSkeleton />}

        {/* ── Error ───────────────────────────────────────────────────── */}
        {isError && !isLoading && (
          <div className={styles.errorBlock} role="alert" data-testid="sku-detail-error">
            <p className={styles.errorMessage}>{t('skuDetail.loadError')}</p>
            <button
              type="button"
              className={styles.retryBtn}
              onClick={() => void refetch()}
              data-testid="sku-detail-retry-btn"
            >
              {t('common:actions.retry')}
            </button>
          </div>
        )}

        {/* ── Not found / HIDDEN ──────────────────────────────────────── */}
        {!isLoading && !isError && (sku === null || sku?.status === 'HIDDEN') && (
          <NotFoundState />
        )}

        {/* ── Resolved + visible ─────────────────────────────────────── */}
        {!isLoading && !isError && sku != null && sku.status !== 'HIDDEN' && (
          <>
            {/* Hero block */}
            <div className={styles.heroBlock}>
              <div className={styles.sigilWrap}>
                <LivingSigil size={220} state="breathing" archetype={sku.code} />
              </div>
              <div className={styles.heroText}>
                <p className={styles.eyebrow}>{t('skuDetail.eyebrow')}</p>
                <h2
                  id="sku-title"
                  className={styles.title}
                  data-testid="sku-detail-title"
                >
                  {skuLabel(sku.nameKey)}
                </h2>
                <p className={styles.desc} data-testid="sku-detail-desc">
                  {skuLabel(sku.descKey)}
                </p>
              </div>
            </div>

            {/* Meta chips */}
            <div className={styles.metaRow}>
              <span
                className={styles.chip}
                data-testid="sku-detail-category-chip"
              >
                {sku.category.toLowerCase()}
              </span>
              <PriceChip
                priceStars={sku.priceStars}
                releaseTarget={sku.releaseTarget}
                status={sku.status}
              />
            </div>

            {/* CTA — renders nothing if HIDDEN (guard above ensures we never reach here hidden) */}
            <CtaArea code={sku.code} status={sku.status} />
          </>
        )}
      </main>
    </div>
  );
}
